import { Location } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, filter, Subscription } from 'rxjs';

import {
  AddressViewModel,
  SiteContextListResponse,
} from '@app/api/marketplace/models';
import { MerchantSiteViewModel } from '@app/api/marketplace/models/merchant-site-view-model';
import { ImpersonationSelectors } from '@app/easylot-admin/modules/impersonation/store/impersonation.slice';
import {
  AuthenticationActions,
  AuthenticationSelectors,
} from '@authentication/store/authentication.slice';
import { ContextSwitcherComponent } from '@layout/components/header/context-switcher/context-switcher.component';
import {
  MerchantActions,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';

@Component({
  selector: 'app-context',
  templateUrl: './context.component.html',
  styleUrls: ['./context.component.scss'],
})
export class ContextComponent implements OnInit, OnDestroy {
  @ViewChild('expansionPanel') panel?: MatExpansionPanel;

  subs = new Subscription();
  sites: MerchantSiteViewModel[] = [];
  selectedSite?: MerchantSiteViewModel;
  switcherOpened = false;
  inImpersonation = false;
  selectedMerchantType?:
    | 'MANUFACTURER_OR_IMPORTER_AS_TAX_WAREHOUSE'
    | 'MANUFACTURER_OR_IMPORTER_AS_SIMPLIFIED_TAX_WAREHOUSE'
    | 'MANUFACTURER_OR_IMPORTER_AS_SMALL_SCALE_WINE_PRODUCER'
    | 'INTERNATIONAL_TRADER_AS_REGISTERED_DEALER'
    | 'INTERNATIONAL_TRADER_AS_COMMUNITY_EXCISE_WHOLESALER'
    | 'INTERNATIONAL_TRADER_AS_EXPORT_IMPORT_WHOLESALER'
    | 'WHOLESALER_AS_IS'
    | 'RETAILER_AS_HOTEL'
    | 'RETAILER_AS_RESTAURANT'
    | 'RETAILER_AS_CAFE'
    | 'RETAILER_AS_RETAIL_CHAIN'
    | 'RETAILER_AS_TRAFIK'
    | 'RETAILER_AS_OTHER_RETAILER';

  choosableContexts?: Array<
    SiteContextListResponse & { address: AddressViewModel }
  >;

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.panel?.close();
    }
  }

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private eRef: ElementRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.store.dispatch(MerchantActions.loadSites.trigger());
    this.store.dispatch(AuthenticationActions.getChoosableContexts.trigger());
    this.store.dispatch(AuthenticationActions.getUserInfo.trigger());

    this.subs.add(
      this.store
        .select(ImpersonationSelectors.selectImpersonatedMerchantId)
        .subscribe((id) => {
          if (id) {
            this.inImpersonation = true;
          } else {
            if (this.inImpersonation) {
              this.inImpersonation = false;
              this.subs.unsubscribe();
            }
          }
        })
    );

    this.subs.add(
      combineLatest([
        this.store.select(MerchantSelectors.selectSiteList),
        this.store.select(AuthenticationSelectors.selectUserInfo),
        this.store.select(MerchantSelectors.selectSitesLoaded),
        this.store.select(AuthenticationSelectors.selectChoosableContexts),
      ])
        .pipe(filter(([_sites, _sess, loaded, _contexts]) => loaded))
        .subscribe(([sites, sess, _loaded, contexts]) => {
          if (Object.values(sess).length) {
            this.sites = sites;
            const selectedSiteId = sess.attributes?.['custom:selectedSiteId'];
            this.selectedSite = this.sites.find((s) => s.id === selectedSiteId);
            this.selectedMerchantType =
              sess.attributes?.['custom:selectedMerchantType'];
            this.choosableContexts = contexts
              .map((context) => ({
                ...context,
                address: {
                  ...this.sites.find((site) => site.id === context.siteId)
                    ?.address!,
                },
              }))
              .filter(
                (context) =>
                  context.siteId !== this.selectedSite?.id ||
                  this.selectedMerchantType !== context.merchantType
              );

            if (
              (this.router.url.includes('buying') ||
                this.router.url.includes('selling')) &&
              (!this.selectedSite || !this.selectedMerchantType)
            ) {
              this.handleMissingInfo();
            }
          }
        })
    );

    this.subs.add(
      this.router.events.subscribe((e) => {
        if (
          e instanceof NavigationEnd &&
          (e.url.includes('buying') || e.url.includes('selling')) &&
          (!this.selectedSite || !this.selectedMerchantType)
        ) {
          this.handleMissingInfo();
        }
      })
    );
  }

  switchContext(
    context: SiteContextListResponse & { address: AddressViewModel }
  ) {
    this.store.dispatch(
      AuthenticationActions.contextSwitch.trigger({
        data: {
          merchantType: context.merchantType!,
          siteId: context.siteId!,
        },
      })
    );

    this.panel?.close();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private handleMissingInfo() {
    if (!this.switcherOpened) {
      this.switcherOpened = true;
      this.store.dispatch(
        AuthenticationActions.toggleOpenedSwitcher.trigger({ opened: true })
      );
      this.dialog
        .open(ContextSwitcherComponent, { width: '600px' })
        .afterClosed()
        .subscribe((res) => {
          this.switcherOpened = false;
          if (!res) {
            if (this.router.url !== '/admin/impersonation') {
              history.back();
            }
          }
        });
    }
  }
}
